import React, { useState, useRef, useEffect } from 'react';
import { GraduationCap } from 'lucide-react';
import AddEducationModal from '../components/AddEducationModal';
import ConfirmDeleteModal from '../../../organisms/ConfirmDeleteModal';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS } from '../../../../config';
import { api } from '../../../../utils/api';
import '../../../../styles/profile/education-tab.css';

interface EducationData {
  uid?: string;
  institutionName: string;
  degree: string;
  fieldOfStudy: string;
  graduationDate: string;
  description: string;
  grade: string;
  achievements: string[];
}

interface Education {
  uid: string;
  institution_name: string;
  degree: string | null;
  field_of_study: string | null;
  graduation_date: string | null;
  description: string | null;
  grade: string | null;
  achievements: string[] | null;
  created_at: string;
  updated_at: string;
}

interface UserData {
  education: Education[] | null;
}

interface EducationTabProps {
  userData: UserData | null;
  education: Education[];
}

interface EducationCardProps {
  education: Education;
  onEdit: (education: Education) => void;
  onDelete: (uid: string) => void;
}

function EducationCard({ education, onEdit, onDelete }: EducationCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const graduationDate = formatDate(education.graduation_date);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="education-card">
      <div className="education-card-header">
        <div className="education-card-logo">
          <GraduationCap className="education-card-logo-icon" size={20} />
        </div>
        <div className="education-card-info">
          <h3 className="education-card-title">{education.institution_name}</h3>
          {education.degree && education.field_of_study && (
            <p className="education-card-subtitle">
              {education.degree} in {education.field_of_study}
            </p>
          )}
          {education.degree && !education.field_of_study && (
            <p className="education-card-subtitle">{education.degree}</p>
          )}
          {!education.degree && education.field_of_study && (
            <p className="education-card-subtitle">{education.field_of_study}</p>
          )}
        </div>
        {graduationDate && <p className="education-card-date">Graduated: {graduationDate}</p>}
        <div className="education-card-menu-container" ref={menuRef}>
          <button 
            className="education-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="education-card-dropdown">
              <button
                className="education-card-dropdown-item"
                onClick={() => {
                  onEdit(education);
                  setMenuOpen(false);
                }}
              >
                Edit education
              </button>
              <button
                className="education-card-dropdown-item education-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(education.uid);
                  setMenuOpen(false);
                }}
              >
                Delete education
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="education-card-body">
        {education.grade && (
          <p className="education-card-grade">
            <span className="education-card-grade-label">Grade:</span> {education.grade}
          </p>
        )}
        {education.description && (
          <p className="education-card-description">{education.description}</p>
        )}
        {education.achievements && education.achievements.length > 0 && (
          <div className="education-card-achievements">
            <p className="education-card-achievements-label">Achievements:</p>
            <ul className="education-card-achievements-list">
              {education.achievements.map((achievement, idx) => (
                <li key={idx}>{achievement}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EducationTab({ userData, education }: EducationTabProps) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [userEducation, setUserEducation] = useState<Education[]>([]);
  const [selectedEducation, setSelectedEducation] = useState<Education | null>(null);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);

  useEffect(() => {
    if (education && education.length > 0) {
      setUserEducation(education);
    }
  }, [education]);

  const handleImport = () => {
  };

  const handleAdd = () => {
    setSelectedEducation(null);
    setAddModalOpen(true);
  };

  const handleEdit = (education: Education) => {
    setSelectedEducation(education);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (uid: string) => {
    setDeleteUid(uid);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUid) return;

    setIsDeleting(true);

    try {
      
      await api.delete(API_ENDPOINTS.users.deleteEducation(deleteUid));
      
      setUserEducation(userEducation.filter(edu => edu.uid !== deleteUid));
      setToast({ message: 'Education deleted successfully! ✓', type: 'success' });
      setDeleteModalOpen(false);
      setDeleteUid(null);
    } catch (error) {
      setToast({ message: 'Failed to delete education. Please try again.', type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddEducation = async (data: EducationData) => {
    setIsCreating(true);

    try {
      const payload: any = {
        institution_name: data.institutionName,
      };

      if (data.uid) {
        payload.uid = data.uid;
      }

      if (data.degree.trim()) payload.degree = data.degree.trim();
      if (data.fieldOfStudy.trim()) payload.field_of_study = data.fieldOfStudy.trim();
      if (data.graduationDate) payload.graduation_date = data.graduationDate;
      if (data.description.trim()) payload.description = data.description.trim();
      if (data.grade.trim()) payload.grade = data.grade.trim();
      if (data.achievements && data.achievements.length > 0) {
        payload.achievements = data.achievements.filter(a => a.trim() !== '');
      }

      const isEditMode = !!data.uid;

      const response = await api.post(API_ENDPOINTS.users.updateEducation, payload);
      

      if (isEditMode) {
        setUserEducation(userEducation.map(edu => 
          edu.uid === data.uid ? { ...edu, ...response } : edu
        ));
        setToast({ message: 'Education updated successfully! ✓', type: 'success' });
        setEditModalOpen(false);
        setSelectedEducation(null);
      } else {
        if (response && response.uid) {
          setUserEducation([...userEducation, response]);
        }
        setToast({ message: 'Education added successfully! ✓', type: 'success' });
        setAddModalOpen(false);
      }
    } catch (error) {
      setToast({ message: 'Failed to save education. Please try again.', type: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const educationToModalData = (edu: Education | null): any => {
    if (!edu) return null;
    return {
      uid: edu.uid,
      institution_name: edu.institution_name,
      degree: edu.degree,
      field_of_study: edu.field_of_study,
      graduation_date: edu.graduation_date,
      description: edu.description,
      grade: edu.grade,
      achievements: edu.achievements,
    };
  };

  return (
    <>
      <div className="education-tab">
        <div className="education-tab-header">
          <h1 className="education-tab-title">Education</h1>
          <button className="education-tab-btn education-tab-btn--import" onClick={handleImport} type="button">
            <span className="education-tab-btn-icon">⬇</span>
            Import
          </button>
        </div>

        <div className="education-tab-content">
          <button 
            type="button" 
            className="education-tab-add-btn"
            onClick={handleAdd}
          >
            <span className="education-tab-add-icon">+</span>
          </button>

          {userEducation.length > 0 && (
            <div className="education-list">
              {userEducation.map((edu) => (
                <EducationCard 
                  key={edu.uid} 
                  education={edu}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddEducationModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddEducation}
      />

      <AddEducationModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedEducation(null);
        }}
        onAdd={handleAddEducation}
        title="Edit Education"
        initialData={educationToModalData(selectedEducation)}
        isEditMode={true}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        itemName={userEducation.find(edu => edu.uid === deleteUid)?.institution_name || 'this education'}
        itemType="Education"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteUid(null);
        }}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
